import { AppViewMode } from "../../types/user/app-view-mode";
import { ParkingLot } from "../../types/user/parking-lot";
import { Role } from "../../types/user/role";
import * as XLSX from 'xlsx';

interface ExcelUserData {
    name: string;
    phone: string;
    username: string;
    password: string;
    birth: string;
    scheduleAlias: string;
    parkinglot: string;
    appViewMode: string;
    hasAuthorityToModifyOrder: string;
}

export interface ValidatedUserData {
    name: string;
    phone: string;
    role: Role;
    birth: string;
    username: string;
    password: string;
    parkinglot: ParkingLot;
    scheduleAlias: string;
    appViewMode: AppViewMode;
    clientIds: string[];
    agencyIds: string[];
    companyId: string;
    hasAuthorityToModifyOrder: boolean;
}

const HEADER_MAP = {
    '배송원 이름': 'name',
    '핸드폰 번호': 'phone',
    '앱 아이디': 'username',
    '앱 비밀번호': 'password',
    '생년월일': 'birth',
    '스케줄 라인명': 'scheduleAlias',
    '주차 유형': 'parkinglot',
    '앱 표기': 'appViewMode',
    '앱에서 배송 순서 수정 권한을 허용': 'hasAuthorityToModifyOrder'
} as const;

export const validateExcelData = (file: File, companyId: string): Promise<ValidatedUserData[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                // 헤더를 포함한 모든 데이터를 읽어옴
                const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // 첫 번째 행(헤더)을 이용해서 컬럼 매핑
                const headers = rawData[0] as string[];
                const columnMap = headers.reduce((acc, header, index) => {
                    const key = HEADER_MAP[header as keyof typeof HEADER_MAP];
                    if (key) acc[key] = index;
                    return acc;
                }, {} as Record<string, number>);

                // 필수 컬럼 체크
                const requiredColumns = ['name', 'phone', 'username', 'password'];
                const missingColumns = requiredColumns.filter(col => columnMap[col] === undefined);
                if (missingColumns.length > 0) {
                    throw new Error(`필수 컬럼이 없습니다: ${missingColumns.join(', ')}`);
                }

                // 데이터 행 처리
                const jsonData = rawData.slice(1)
                    .filter((row: any) => {
                        // 모든 셀이 비어있는 행은 제외
                        return Object.values(columnMap).some(index => row[index] !== undefined);
                    })
                    .map((row: any, rowIndex: number) => {
                        const getValue = (key: string) => {
                            const value = row[columnMap[key]];
                            return value === undefined ? "" : value;
                        };

                        return {
                            name: getValue('name'),
                            phone: getValue('phone'),
                            username: getValue('username'),
                            password: getValue('password'),
                            birth: getValue('birth'),
                            scheduleAlias: getValue('scheduleAlias'),
                            parkinglot: getValue('parkinglot'),
                            appViewMode: getValue('appViewMode'),
                            hasAuthorityToModifyOrder: getValue('hasAuthorityToModifyOrder') || 'FALSE'
                        };
                    });

                const validatedData = jsonData.map((row, index) => {
                    // 데이터 검증
                    if (!row.name) throw new Error(`${index + 2}행: 이름은 필수입니다.`);
                    if (!row.phone) throw new Error(`${index + 2}행: 전화번호는 필수입니다.`);
                    if (!row.username) throw new Error(`${index + 2}행: 앱 아이디는 필수입니다.`);
                    if (!row.password) throw new Error(`${index + 2}행: 앱 비밀번호는 필수입니다.`);

                    // 전화번호 하이픈 제거
                    const cleanPhone = row.phone.toString().replace(/-/g, '');
                    if (!/^\d{10,11}$/.test(cleanPhone)) {
                        throw new Error(`${index + 2}행: 올바른 전화번호 형식이 아닙니다.`);
                    }

                    // 생년월일 검증
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(row.birth)) {
                        throw new Error(`${index + 2}행: 생년월일은 YYYY-MM-DD 형식이어야 합니다.`);
                    }

                    // 주차 유형 검증
                    if (!['법인 차량', '자차'].includes(row.parkinglot)) {
                        throw new Error(`${index + 2}행: 주차 유형은 '법인 차량' 또는 '자차'여야 합니다.`);
                    }

                    // 앱 표기 검증
                    if (!['B2B', 'B2C'].includes(row.appViewMode)) {
                        throw new Error(`${index + 2}행: 앱 표기는 'B2B' 또는 'B2C'여야 합니다.`);
                    }

                    // 권한 검증
                    const hasAuthority = row.hasAuthorityToModifyOrder?.toString().toUpperCase();
                    if (!['TRUE', 'FALSE'].includes(hasAuthority)) {
                        throw new Error(`${index + 2}행: 배송 순서 수정 권한은 'TRUE' 또는 'FALSE'여야 합니다.`);
                    }

                    return {
                        name: row.name,
                        phone: cleanPhone,
                        role: Role.Pro,
                        birth: row.birth,
                        username: row.username,
                        password: row.password,
                        parkinglot: row.parkinglot === '법인 차량' ? ParkingLot.Company : ParkingLot.Private,
                        scheduleAlias: row.scheduleAlias,
                        appViewMode: row.appViewMode === 'B2B' ? AppViewMode.Onggoing : AppViewMode.Hyundai,
                        clientIds: [],
                        agencyIds: [],
                        companyId,
                        hasAuthorityToModifyOrder: row.appViewMode === 'B2B' ? false : hasAuthority === 'TRUE',
                    };
                });

                resolve(validatedData);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error('파일을 읽는 중 오류가 발생했습니다.'));
        };

        reader.readAsArrayBuffer(file);
    });
}; 